import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Printer,
  Save,
  FileText,
  CheckSquare,
  Square,
  Download,
  MessageCircle,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/project/lib/auth";
import { inr, shortDate } from "@/project/lib/format";
import {
  orgsQuery,
  settingsQuery,
  checklistQuery,
} from "@/project/lib/queries";
import {
  ApplicationForm,
  emptyApplication,
  toRow,
  validateApplication,
  type AppFormValues,
} from "@/project/components/ApplicationForm";
import { LetterSheet } from "@/project/components/LetterSheet";
import {
  applicationDocHtml,
  printHtmlDocument,
  shareByEmail,
  shareOnWhatsApp,
} from "@/project/lib/print-doc";
import { MemberPicker } from "@/project/components/MemberPicker";

export const Route = createFileRoute("/applications/$id")({
  head: () => ({
    meta: [
      { title: "Application Detail · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Review assistance application details, select partner organizations and generate recommendation letters.",
      },
      { property: "og:title", content: "Application Detail" },
      {
        property: "og:description",
        content:
          "Review an assistance application and generate recommendation letters.",
      },
    ],
  }),
  component: ApplicationDetailPage,
});

function ApplicationDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<AppFormValues | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const { data: app } = useQuery({
    queryKey: ["application", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select(
          "*, assistance_categories(name), application_organizations(organization_id, requested_amount)"
        )
        .eq("id", id)
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
  });
  const { data: orgs } = useQuery(orgsQuery);
  const { data: settings } = useQuery(settingsQuery);
  const { data: checklist } = useQuery(checklistQuery);
  const { data: letters } = useQuery({
    queryKey: ["letters", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("generated_letters")
        .select("*")
        .eq("application_id", id)
        .order("generated_at", { ascending: false });
      return data ?? [];
    },
  });
  const { data: members } = useQuery({
    queryKey: ["family-members", app?.family_id],
    enabled: Boolean(app?.family_id),
    queryFn: async () => {
      const { data } = await supabase
        .from("family_members")
        .select("*")
        .eq("family_id", app!.family_id!)
        .order("created_at");
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!app) {
      return;
    }
    setForm({
      ...emptyApplication(),
      application_date: app.application_date,
      ration_card_no: app.ration_card_no ?? "",
      applicant_name: app.applicant_name,
      applicant_address: app.applicant_address ?? "",
      applicant_mobile: app.applicant_mobile ?? "",
      applicant_alt_mobile: app.applicant_alt_mobile ?? "",
      applicant_aadhaar: app.applicant_aadhaar ?? "",
      same_as_applicant: app.same_as_applicant,
      beneficiary_name: app.beneficiary_name ?? "",
      beneficiary_relation: app.beneficiary_relation ?? "",
      beneficiary_mobile: app.beneficiary_mobile ?? "",
      beneficiary_aadhaar: app.beneficiary_aadhaar ?? "",
      category_id: app.category_id ?? "",
      subcategory: app.subcategory ?? "",
      purpose: app.purpose ?? "",
      total_requirement: String(app.total_requirement ?? ""),
      applicant_contribution: String(app.applicant_contribution ?? ""),
      our_contribution: String(app.our_contribution ?? ""),
      other_received: String(app.other_received ?? ""),
      bank_account_holder: app.bank_account_holder ?? "",
      bank_name: app.bank_name ?? "",
      bank_branch: app.bank_branch ?? "",
      bank_account_no: app.bank_account_no ?? "",
      bank_ifsc: app.bank_ifsc ?? "",
      bank_account_type: app.bank_account_type ?? "",
      disbursed_bank: app.disbursed_bank ?? "",
      issuing_organization: app.issuing_organization ?? "",
      passbook_uploaded: app.passbook_uploaded,
      remarks: app.remarks ?? "",
      status: app.status,
    });
    setSelected(
      (app.application_organizations ?? []).map(o => o.organization_id)
    );
    setAmounts(
      Object.fromEntries(
        (app.application_organizations ?? []).map(o => [
          o.organization_id,
          o.requested_amount ? String(o.requested_amount) : "",
        ])
      )
    );
  }, [app]);

  const activeOrgs = (orgs ?? []).filter(o => o.active);
  const selectedOrgs = useMemo(
    () => activeOrgs.filter(o => selected.includes(o.id)),
    [activeOrgs, selected]
  );
  const documents = useMemo(() => {
    const active = (checklist ?? []).filter(d => d.active);
    // Category-specific items plus the general ones that apply to every case.
    const scoped = active.filter(
      d => d.category_id === app?.category_id || d.category_id === null
    );
    return (scoped.length ? scoped : active).map(d => d.document_name);
  }, [checklist, app?.category_id]);

  const allocated = useMemo(
    () => selected.reduce((s, id) => s + Number(amounts[id] || 0), 0),
    [selected, amounts]
  );
  const balanceRequired = Number(app?.balance_required ?? 0);
  const unallocated = balanceRequired - allocated;

  const save = async () => {
    if (!form) {
      return;
    }
    const err = validateApplication(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("applications")
      .update(toRow(form, user?.id))
      .eq("id", id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Application updated");
    qc.invalidateQueries({ queryKey: ["application", id] });
  };

  const saveSelection = async () => {
    await supabase
      .from("application_organizations")
      .delete()
      .eq("application_id", id);
    if (selected.length) {
      await supabase.from("application_organizations").insert(
        selected.map(organization_id => ({
          application_id: id,
          organization_id,
          requested_amount: Number(amounts[organization_id] || 0),
        }))
      );
    }
    toast.success(
      `${selected.length} organization(s) saved for this application`
    );
  };

  const generateLetters = async () => {
    if (!selected.length) {
      toast.error("Select at least one organization before generating letters");
      return;
    }
    await saveSelection();
    const days = Number(settings?.["letter_validity_days"] || 15);
    const validUntil = new Date(Date.now() + days * 86400000)
      .toISOString()
      .slice(0, 10);
    const { error } = await supabase.from("generated_letters").insert(
      selectedOrgs.map(o => ({
        application_id: id,
        organization_id: o.id,
        organization_name: o.short_name || o.name,
        requested_amount: Number(amounts[o.id] || 0),
        letter_date:
          app?.application_date ?? new Date().toISOString().slice(0, 10),
        valid_until: validUntil,
        created_date:
          app?.created_at?.slice(0, 10) ??
          new Date().toISOString().slice(0, 10),
        generated_by: user?.id ?? null,
      }))
    );
    if (error) {
      toast.error(error.message);
      return;
    }
    await supabase
      .from("applications")
      .update({ status: "Letter Generated" })
      .eq("id", id);
    qc.invalidateQueries({ queryKey: ["letters", id] });
    qc.invalidateQueries({ queryKey: ["application", id] });
    toast.success(`${selectedOrgs.length} letter(s) recorded in history`);
  };

  if (!app || !form) {
    return (
      <Shell>
        <p className="text-muted-foreground">Loading application…</p>
      </Shell>
    );
  }

  const letterMeta = new Map<
    string,
    { created?: string | null; generated?: string | null }
  >();
  for (const l of letters ?? []) {
    if (l.organization_id && !letterMeta.has(l.organization_id)) {
      letterMeta.set(l.organization_id, {
        created: (l as { created_date?: string | null }).created_date ?? null,
        generated: l.generated_at?.slice(0, 10) ?? null,
      });
    }
  }

  const letterApp = {
    ref_no: app.ref_no,
    application_date: app.application_date,
    applicant_name: app.applicant_name,
    applicant_address: app.applicant_address,
    applicant_mobile: app.applicant_mobile,
    applicant_aadhaar: app.applicant_aadhaar,
    ration_card_no: app.ration_card_no,
    beneficiary_name: app.beneficiary_name,
    total_requirement: Number(app.total_requirement),
    applicant_contribution: Number(app.applicant_contribution),
    our_contribution: Number(app.our_contribution),
    other_received: Number(app.other_received),
    balance_required: Number(app.balance_required),
    bank_account_holder: app.bank_account_holder,
    bank_name: app.bank_name,
    bank_account_no: app.bank_account_no,
    bank_ifsc: app.bank_ifsc,
    categoryName: app.assistance_categories?.name ?? "",
  };

  const downloadPdf = () =>
    printHtmlDocument(
      `Application-${app.ref_no}`,
      applicationDocHtml(
        { ...app, categoryName: app.assistance_categories?.name },
        settings ?? {},
        selectedOrgs.map(o => ({
          name: o.short_name || o.name,
          amount: Number(amounts[o.id] || 0),
        }))
      )
    );

  const orgMessage = (o: (typeof activeOrgs)[number]) =>
    [
      `*${settings?.["org_heading"] ?? "Sadharmik Assistance Desk"}*`,
      `To: ${o.short_name || o.name}`,
      `Recommendation letter ref. ${app.ref_no} dated ${shortDate(app.application_date)}`,
      `Beneficiary: ${app.beneficiary_name || app.applicant_name}`,
      `Amount requested: ${inr(Number(amounts[o.id] || 0))}`,
      `Balance required: ${inr(app.balance_required)}`,
      "",
      "Please find the recommendation letter attached.",
    ].join("\n");

  return (
    <Shell>
      <PageHeader
        title={`Application Ref. ${app.ref_no}`}
        subtitle={`${app.applicant_name} · ${shortDate(app.application_date)} · Balance ${inr(app.balance_required)}`}
        actions={
          <>
            <Button variant="outline" onClick={downloadPdf}>
              <Download className="mr-2 h-4 w-4" /> Download PDF (A5)
            </Button>
            <Badge variant="secondary">{app.status}</Badge>
          </>
        }
      />

      <Tabs defaultValue="details">
        <TabsList className="no-print">
          <TabsTrigger value="details">Application Details</TabsTrigger>
          <TabsTrigger value="letters">
            Organizations &amp; Letters ({selected.length})
          </TabsTrigger>
          <TabsTrigger value="history">Letter History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="pt-4">
          <div className="mb-4">
            <MemberPicker
              members={members ?? []}
              value={form}
              onChange={setForm}
            />
          </div>
          <ApplicationForm value={form} onChange={setForm} />
          <div className="no-print bg-background/95 sticky bottom-0 mt-4 flex gap-3 border-t border-border py-4 backdrop-blur">
            <Button size="lg" disabled={saving} onClick={save}>
              <Save className="mr-2 h-4 w-4" /> Save Changes
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="letters" className="pt-4">
          <Card className="no-print mb-4">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">
                Select Partner Organizations — {selected.length} selected
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(activeOrgs.map(o => o.id))}
                >
                  <CheckSquare className="mr-2 h-4 w-4" /> Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected([])}
                >
                  <Square className="mr-2 h-4 w-4" /> Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {activeOrgs.map(o => (
                <label
                  key={o.id}
                  className="hover:bg-muted/50 flex items-start gap-3 rounded-md border border-border p-3 text-sm"
                >
                  <Checkbox
                    checked={selected.includes(o.id)}
                    onCheckedChange={c =>
                      setSelected(prev =>
                        c ? [...prev, o.id] : prev.filter(x => x !== o.id)
                      )
                    }
                  />
                  <span className="flex-1">
                    <span className="font-medium">
                      {o.short_name || o.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {o.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {o.visiting_day} {o.visiting_time}
                    </span>
                    {selected.includes(o.id) && (
                      <span className="mt-2 block">
                        <span className="mb-1 block text-xs uppercase text-muted-foreground">
                          Amount requested from this institution
                        </span>
                        <Input
                          inputMode="numeric"
                          placeholder="e.g. 25000"
                          value={amounts[o.id] ?? ""}
                          onChange={e =>
                            setAmounts(prev => ({
                              ...prev,
                              [o.id]: e.target.value,
                            }))
                          }
                        />
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </CardContent>
            <div className="flex flex-wrap gap-4 border-t border-border px-6 py-3 text-sm">
              <span>
                Balance required: <strong>{inr(balanceRequired)}</strong>
              </span>
              <span>
                Allocated across {selected.length} institution(s):{" "}
                <strong>{inr(allocated)}</strong>
              </span>
              <span
                className={
                  unallocated > 0 ? "text-destructive" : "text-muted-foreground"
                }
              >
                {unallocated > 0
                  ? `Still to be covered: ${inr(unallocated)} — add another institution`
                  : unallocated < 0
                    ? `Over-allocated by ${inr(Math.abs(unallocated))}`
                    : "Fully covered"}
              </span>
            </div>
          </Card>

          <div className="no-print mb-4 flex flex-wrap gap-3">
            <Button size="lg" onClick={generateLetters}>
              <FileText className="mr-2 h-4 w-4" /> Generate Recommendation
              Letters
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 h-4 w-4" /> Print All Letters
            </Button>
            <Button size="lg" variant="outline" onClick={saveSelection}>
              Save Selection
            </Button>
          </div>

          <div className="space-y-6">
            {selectedOrgs.map((o, i) => (
              <div key={o.id}>
                <div className="no-print mb-1 flex flex-wrap items-center gap-2">
                  <p className="text-xs text-muted-foreground">
                    Letter {i + 1} of {selectedOrgs.length} —{" "}
                    {o.short_name || o.name}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      shareOnWhatsApp(o.contact_number, orgMessage(o))
                    }
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      shareByEmail(
                        o.email,
                        `Recommendation letter ${app.ref_no} — ${app.applicant_name}`,
                        orgMessage(o).replace(/\*/g, "")
                      )
                    }
                  >
                    <Mail className="mr-2 h-4 w-4" /> Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.print()}
                  >
                    <Printer className="mr-2 h-4 w-4" /> Print A5
                  </Button>
                </div>
                <LetterSheet
                  app={letterApp}
                  org={o}
                  allOrgs={activeOrgs}
                  amounts={amounts}
                  documents={documents}
                  settings={settings ?? {}}
                  createdDate={letterMeta.get(o.id)?.created ?? null}
                  generatedDate={letterMeta.get(o.id)?.generated ?? null}
                />
              </div>
            ))}
            {!selectedOrgs.length && (
              <p className="text-sm text-muted-foreground">
                Select organizations above to preview the letters.
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="pt-4">
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="data-table w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Organization</th>
                    <th className="px-4 py-2">Letter Created</th>
                    <th className="px-4 py-2">Letter Date</th>
                    <th className="px-4 py-2">Valid Until</th>
                    <th className="px-4 py-2">Generated At</th>
                  </tr>
                </thead>
                <tbody>
                  {(letters ?? []).map(l => (
                    <tr key={l.id} className="border-t border-border">
                      <td className="px-4 py-2">{l.organization_name}</td>
                      <td className="px-4 py-2">{shortDate(l.created_date)}</td>
                      <td className="px-4 py-2">{shortDate(l.letter_date)}</td>
                      <td className="px-4 py-2">{shortDate(l.valid_until)}</td>
                      <td className="px-4 py-2">
                        {new Date(l.generated_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {!letters?.length && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No letters generated yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="no-print mt-6 text-sm">
        <Link to="/applications" className="underline">
          ← Back to all applications
        </Link>
      </p>
    </Shell>
  );
}
